import { useQuery } from "@apollo/client";
import { Button, styled } from "@mui/material";
import { ReactElement } from "react";
import React from "react";
import { FormattedMessage } from "react-intl";

import {
  GetThreadQuery,
  GetThreadQueryVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import Settings from "~frontend/components/icons/Settings";
import { ThreadDetailsWrapper } from "~frontend/components/threads";
import { GET_THREAD } from "~frontend/graphql/threads";
import { useTexterSettings } from "~frontend/providers/TexterSettingsProvider";
import { palette } from "~frontend/theme";

import ThreadControls from "../../../components/threads/ThreadControls";
import ThreadMessageList, {
  ThreadMessageListRef,
} from "../../../components/threads/ThreadMessageList";
import { useThreadsContext } from "../../../components/threads/ThreadsProvider";

const EmptyStateContainer = styled("div")({
  flex: 1,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  fontStyle: "italic",
});

export default function ThreadDetails(): ReactElement {
  const { threadId } = useThreadsContext()!;
  const { showTexterSettingsDialog } = useTexterSettings();

  const { data, loading, error, refetch } = useQuery<
    GetThreadQuery,
    GetThreadQueryVariables
  >(GET_THREAD, {
    variables: {
      input: {
        threadId: threadId!,
      },
    },
    skip: !threadId,
    fetchPolicy: "network-only",
  });

  const messageListRef = React.useRef<ThreadMessageListRef>(null);

  if (!threadId) {
    return (
      <EmptyStateContainer>
        <FormattedMessage
          defaultMessage="Select a thread to view messages"
          description="Empty state when no thread is selected"
        />
      </EmptyStateContainer>
    );
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <ThreadDetailsWrapper>
      <ThreadMessageList
        thread={data!.getThread}
        ref={messageListRef}
        showCampaignContexts
      />

      <FlexBox
        alignItems="center"
        sx={{
          backgroundColor: palette.lightestGray,
          marginLeft: "24px",
          position: "absolute",
          top: "24px",
          right: "8px",
        }}
      >
        <Button
          onClick={showTexterSettingsDialog}
          sx={{ minWidth: "40px", minHeight: "40px" }}
        >
          <Settings
            sx={{ color: palette.darkGray, width: "20px", height: "20px" }}
          />
        </Button>
      </FlexBox>
      <ThreadControls
        thread={data!.getThread}
        refetchThread={refetch}
        scrollToCampaign={campaignId =>
          messageListRef.current?.scrollToSearchMatch({
            campaignIds: [campaignId],
          })
        }
        scrollToLatest={() => messageListRef.current?.scrollToLatest()}
      />
    </ThreadDetailsWrapper>
  );
}
